import React, { useRef } from 'react'
import { motion } from "motion/react"
import useAudioManager from '../hooks/useAudioManager'
import { useAppUpdateStore } from '../services/appUpdateService'

/**
 * Универсальная анимированная кнопка для главных целевых действий
 * @param {Object} props
 * @param {Function} props.onClick - Обработчик клика
 * @param {React.ReactNode} props.children - Содержимое кнопки
 * @param {boolean} props.disabled - Состояние disabled
 * @param {string} props.className - Дополнительные CSS классы
 * @param {boolean} props.hapticFeedback - Включить вибрацию при клике (по умолчанию true)
 * @param {boolean} props.soundFeedback - Включить звук клика (по умолчанию true)
 */
const AnimatedButton = ({ 
  onClick, 
  children, 
  disabled = false, 
  className = '', 
  hapticFeedback = true,
  soundFeedback = true
}) => {
  const clickLockRef = useRef(false)
  const lockDurationMs = 500

  // Инициализация звука клика через глобальный audioService
  const { playSound } = useAudioManager({
    click: 'buttonClick'
  })

  // Функция вибрации
  const triggerHaptic = () => {
    if (!hapticFeedback) return
    
    try {
      const haptic = window.Telegram?.WebApp?.HapticFeedback
      if (haptic && typeof haptic.impactOccurred === 'function') {
        haptic.impactOccurred('heavy')
      }
    } catch (error) {
      // Игнорируем ошибки вибрации
    }
  }

  const handleClick = (e) => {
    if (disabled || clickLockRef.current || useAppUpdateStore.getState().applying) return
    clickLockRef.current = true
    setTimeout(() => {
      clickLockRef.current = false
    }, lockDurationMs)
    
    // Воспроизводим звук клика
    if (soundFeedback) {
      playSound('click')
    }
    
    // Вибрация
    triggerHaptic()
    
    // Задержка перед вызовом onClick для полного воспроизведения звука
    // Это предотвращает обрыв звука при навигации
    if (soundFeedback) {
      setTimeout(() => {
        if (useAppUpdateStore.getState().applying) return
        onClick?.(e)
      }, 250) // 250ms для полного воспроизведения звука клика
    } else {
      // Если звук отключен, вызываем сразу
      onClick?.(e)
    }
  }

  const baseClassName = disabled
    ? 'w-full py-4 bg-surface text-text-secondary font-bold text-xl rounded-xl overflow-hidden relative border border-white/10'
    : 'w-full py-4 bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary font-bold text-xl rounded-xl overflow-hidden relative shadow-2xl'

  return (
    <motion.button
      onClick={handleClick}
      disabled={disabled}
      className={`${baseClassName} ${disabled ? 'opacity-100 cursor-not-allowed' : ''} ${className}`}
      whileHover={disabled ? {} : { scale: 1.02 }}
      whileTap={disabled ? {} : { scale: 0.95 }}
      animate={disabled
        ? { boxShadow: '0 0 0px rgba(0,0,0,0)' }
        : {
            boxShadow: [
              '0 0 25px rgba(0, 255, 127, 0.4), 0 0 50px rgba(78, 255, 198, 0.25)',
              '0 0 40px rgba(78, 255, 198, 0.6), 0 0 80px rgba(0, 255, 127, 0.4)',
              '0 0 25px rgba(0, 255, 127, 0.4), 0 0 50px rgba(78, 255, 198, 0.25)'
            ]
          }
      }
      transition={disabled
        ? { boxShadow: { duration: 0.4, ease: 'easeOut' }, scale: { duration: 0.1 } }
        : {
            boxShadow: { duration: 2.5, repeat: Infinity, ease: 'easeInOut' },
            scale: { duration: 0.1 }
          }
      }
    >
      {/* Анимированный блик */}
      {!disabled && (
        <motion.div
          className="absolute inset-0 bg-white/20"
          animate={{ x: ['-100%', '100%'] }}
          transition={{
            duration: 3,
            repeat: Infinity,
            repeatDelay: 0,
            ease: "linear"
          }}
        />
      )}
      
      {/* Контент кнопки */}
      <span className="relative z-10 flex items-center justify-center gap-2">{children}</span>
    </motion.button>
  )
}

export default AnimatedButton
